import { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Plus,
  Trash2,
  ClipboardList,
  CalendarRange,
  Clock,
  BadgeDollarSign,
  Banknote,
  CheckCircle2,
  Printer,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DateInput } from "@/components/ui/date-input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { formatDateHu } from "@/components/DeductionFormDialog";
import { openProtectedFile } from "@/components/employee/tabHelpers";

const openSalaryReceipt = (salaryId) => {
  openProtectedFile(
    `/salaries/${salaryId}/payment-receipt.pdf`,
    "application/pdf",
    `ber-kifizetes-${salaryId}.pdf`
  );
};
import { Th, Td } from "@/components/employee/tabHelpers";

const huFmt = new Intl.NumberFormat("hu-HU", { maximumFractionDigits: 0 });

const formatHuf = (n) => (n == null ? "—" : `${huFmt.format(Math.round(n))} Ft`);

export function EfoRegistrationsTab({ employee }) {
  const employeeId = employee._id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [payingItem, setPayingItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/employees/${employeeId}/efo-registrations`
      );
      setItems(data);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült betölteni a bejelentéseket."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeId) load();
  }, [employeeId]);

  const onCreated = (b) => setItems((prev) => [b, ...prev]);

  const onDelete = async (b) => {
    if (!window.confirm("Biztosan törlöd ezt a bejelentést?")) return;
    try {
      await api.delete(
        `/employees/${employeeId}/efo-registrations/${b._id}`
      );
      setItems((prev) => prev.filter((x) => x._id !== b._id));
      toast.success("Bejelentés törölve.");
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni."
      );
    }
  };

  return (
    <section
      data-testid="efo-registrations-section"
      className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]"
    >
      <header className="flex flex-col gap-1 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
            EFO bejelentések
          </p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {items.length === 0
              ? "Még nincs rögzített bejelentés."
              : `${items.length} bejelentés · az órabér automatikusan a munkaviszonyból`}
          </p>
        </div>
        <Button
          size="sm"
          data-testid="new-efo-registration-button"
          onClick={() => setOpenCreate(true)}
          disabled={employee.gross_hourly_rate == null}
        >
          <Plus className="h-4 w-4" />
          Új bejelentés
        </Button>
      </header>

      {loading ? (
        <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="efo-registrations-empty"
          className="flex flex-col items-center gap-3 px-6 py-14 text-center"
        >
          <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
            <ClipboardList className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
          </div>
          <p className="text-sm font-medium">Nincs rögzített bejelentés</p>
          <p className="text-xs text-muted-foreground">
            Add hozzá az első EFO bejelentést — a munkaviszony kezdete, vége és
            munkaidő alapján.
          </p>
          {employee.gross_hourly_rate != null && (
            <Button
              variant="outline"
              size="sm"
              className="mt-2 h-8"
              onClick={() => setOpenCreate(true)}
            >
              <Plus className="h-3.5 w-3.5" /> Új bejelentés
            </Button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="efo-registrations-table">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                <Th>Munkaviszony kezdete</Th>
                <Th>Munkaviszony vége</Th>
                <Th>Munkaidő</Th>
                <Th>Órabér</Th>
                <Th>Bér (brutto)</Th>
                <Th>Státusz</Th>
                <Th>Rögzítette</Th>
                <Th className="pr-4 text-right">Művelet</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const total = (it.work_hours || 0) * (it.hourly_rate || 0);
                return (
                  <tr
                    key={it._id}
                    data-testid={`efo-reg-row-${it._id}`}
                    className="border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]"
                  >
                    <Td className="tabular-nums">{formatDateHu(it.start_date)}</Td>
                    <Td className="tabular-nums">{formatDateHu(it.end_date)}</Td>
                    <Td className="tabular-nums">{it.work_hours} óra</Td>
                    <Td className="tabular-nums">{formatHuf(it.hourly_rate)} / óra</Td>
                    <Td className="tabular-nums font-medium">{formatHuf(total)}</Td>
                    <Td>
                      {it.paid_at ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                          <CheckCircle2 className="h-3 w-3" /> Kifizetve
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">
                          Nyitva
                        </span>
                      )}
                    </Td>
                    <Td className="text-muted-foreground">
                      {it.created_by_name || "—"}
                      <span className="ml-1 text-muted-foreground/70">
                        · {formatDateHu(it.created_at)}
                      </span>
                    </Td>
                    <Td className="pr-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {!it.paid_at && (
                          <Button
                            size="sm"
                            className="h-8 bg-emerald-600 text-white hover:bg-emerald-700"
                            onClick={() => setPayingItem(it)}
                            data-testid={`efo-reg-pay-${it._id}`}
                          >
                            <Banknote className="mr-1 h-3.5 w-3.5" /> Kifizetés
                          </Button>
                        )}
                        {it.paid_at && it.salary_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                            onClick={() => openSalaryReceipt(it.salary_id)}
                            data-testid={`efo-reg-receipt-${it._id}`}
                          >
                            <Printer className="mr-1 h-3.5 w-3.5" /> Nyomtatás
                          </Button>
                        )}
                        {!it.paid_at && (
                          <button
                            type="button"
                            onClick={() => onDelete(it)}
                            data-testid={`efo-reg-delete-${it._id}`}
                            aria-label="Törlés"
                            className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CreateEfoRegistrationDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        employee={employee}
        onCreated={onCreated}
      />

      <PayEfoDialog
        open={!!payingItem}
        registration={payingItem}
        onOpenChange={(v) => { if (!v) setPayingItem(null); }}
        employeeId={employeeId}
        onPaid={(updated) => {
          setPayingItem(null);
          setItems((prev) => prev.map((x) => x._id === updated._id ? updated : x));
        }}
      />
    </section>
  );
}

function CreateEfoRegistrationDialog({
  open,
  onOpenChange,
  employee,
  onCreated,
}) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [workHours, setWorkHours] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setStartDate("");
    setEndDate("");
    setWorkHours("");
    setErr("");
  }, [open]);

  const hourlyRate = employee.gross_hourly_rate;

  const grossTotal = useMemo(() => {
    const h = parseFloat(workHours);
    if (!Number.isFinite(h) || hourlyRate == null) return null;
    return Math.round(h * hourlyRate);
  }, [workHours, hourlyRate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!startDate) return setErr("Munkaviszony kezdete kötelező.");
    if (!endDate) return setErr("Munkaviszony vége kötelező.");
    if (endDate < startDate)
      return setErr("A munkaviszony vége nem lehet a kezdete előtt.");
    const wh = parseFloat(workHours);
    if (!Number.isFinite(wh) || wh <= 0 || wh > 24)
      return setErr("A munkaidő 0 és 24 óra között lehet.");
    setSaving(true);
    try {
      const { data } = await api.post(
        `/employees/${employee._id}/efo-registrations`,
        {
          start_date: startDate,
          end_date: endDate,
          work_hours: wh,
        }
      );
      toast.success("Bejelentés rögzítve.");
      onCreated(data);
      onOpenChange(false);
    } catch (error) {
      setErr(
        formatApiError(error?.response?.data?.detail) || "Mentés sikertelen."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="efo-registration-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>Új EFO bejelentés</DialogTitle>
          <DialogDescription>
            Az órabért az aktív EFO munkaviszony alapján töltjük be.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Munkaviszony kezdete *" testId="efo-reg-start" icon={<CalendarRange className="h-3 w-3" />}>
              <DateInput value={startDate} onChange={setStartDate} />
            </Field>
            <Field label="Munkaviszony vége *" testId="efo-reg-end" icon={<CalendarRange className="h-3 w-3" />}>
              <DateInput value={endDate} onChange={setEndDate} />
            </Field>
          </div>

          <Field label="Munkaidő (óra) *" testId="efo-reg-hours" icon={<Clock className="h-3 w-3" />}>
            <Input
              type="number"
              min="0"
              step="0.5"
              max="24"
              inputMode="decimal"
              value={workHours}
              onChange={(e) => setWorkHours(e.target.value)}
              placeholder="pl. 8"
              data-testid="efo-reg-hours-input"
            />
          </Field>

          <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <BadgeDollarSign className="h-3 w-3" /> Órabér (automatikus)
              </span>
              <span className="tabular-nums font-medium" data-testid="efo-reg-rate-display">
                {hourlyRate != null ? `${formatHuf(hourlyRate)} / óra` : "—"}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between border-t border-border/60 pt-2">
              <span className="text-xs text-muted-foreground">Bér (bruttó)</span>
              <span className="tabular-nums font-semibold" data-testid="efo-reg-total-display">
                {grossTotal != null ? formatHuf(grossTotal) : "—"}
              </span>
            </div>
          </div>

          {err && (
            <div
              data-testid="efo-reg-error"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
            >
              {err}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Mégse
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="efo-reg-submit"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Mentés…
                </>
              ) : (
                "Rögzítés"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, testId, icon }) {
  return (
    <label className="block space-y-1.5" data-testid={testId}>
      <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
        {icon}
        {label}
      </span>
      {children}
    </label>
  );
}


function PayEfoDialog({ open, registration, onOpenChange, employeeId, onPaid }) {
  const [method, setMethod] = useState("cash");
  const [registers, setRegisters] = useState([]);
  const [crId, setCrId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMethod("cash");
    setCrId("");
  }, [open]);

  useEffect(() => {
    if (!open || method !== "cash") return;
    (async () => {
      try {
        const siteId = registration?.site_id;
        const { data } = await api.get("/cash-registers", {
          params: siteId ? { site_id: siteId } : undefined,
        });
        const active = (data || []).filter((r) => r.active !== false);
        setRegisters(active);
        if (active.length === 1) setCrId(active[0]._id);
      } catch {
        setRegisters([]);
      }
    })();
  }, [open, method, registration]);

  if (!registration) return null;
  const gross = Math.round(
    (registration.work_hours || 0) * (registration.hourly_rate || 0)
  );

  const onSubmit = async () => {
    if (method === "cash" && !crId) {
      toast.error("Készpénzes kifizetéshez kötelező a házipénztár.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(
        `/employees/${employeeId}/efo-registrations/${registration._id}/pay`,
        {
          payment_method: method,
          cash_register_id: method === "cash" ? crId : null,
        }
      );
      toast.success("EFO bejelentés kifizetve. Bér rekord létrehozva.");
      // Auto-open the printable receipt PDF
      if (data?.salary_id) {
        openSalaryReceipt(data.salary_id);
      }
      onPaid?.(data);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Kifizetés sikertelen."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pay-efo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="h-4 w-4" /> EFO bejelentés kifizetése
          </DialogTitle>
          <DialogDescription>
            Az összeg fix az EFO bejelentés alapján. Sikeres kifizetés után egy
            új <b>EFO Kifizetések</b> kategóriájú bér rekord jön létre a
            munkavállalónál.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border border-border bg-muted/20 p-3">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Időszak / órák
            </p>
            <p className="text-sm">
              {formatDateHu(registration.start_date)} – {formatDateHu(registration.end_date)}
              {" · "}{registration.work_hours} óra × {formatHuf(registration.hourly_rate)}
            </p>
            <p className="mt-2 text-[11px] uppercase tracking-wider text-muted-foreground">
              Bruttó kifizetendő
            </p>
            <p className="text-xl font-bold tabular-nums">{formatHuf(gross)}</p>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider">
              Fizetési mód
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMethod("cash")}
                data-testid="pay-method-cash"
                className={`rounded-lg border px-4 py-3 text-left transition-colors ${
                  method === "cash"
                    ? "border-foreground bg-foreground/[0.04]"
                    : "border-border hover:bg-muted/40"
                }`}
              >
                <p className="text-sm font-semibold">Készpénz</p>
                <p className="text-[11px] text-muted-foreground">Házipénztárból</p>
              </button>
              <button
                type="button"
                onClick={() => setMethod("transfer")}
                data-testid="pay-method-transfer"
                className={`rounded-lg border px-4 py-3 text-left transition-colors ${
                  method === "transfer"
                    ? "border-foreground bg-foreground/[0.04]"
                    : "border-border hover:bg-muted/40"
                }`}
              >
                <p className="text-sm font-semibold">Bankátutalás</p>
                <p className="text-[11px] text-muted-foreground">Banki tranzakció</p>
              </button>
            </div>
          </div>

          {method === "cash" && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">
                Házipénztár *
              </Label>
              <Select value={crId} onValueChange={setCrId}>
                <SelectTrigger data-testid="pay-cash-register">
                  <SelectValue placeholder="Válassz házipénztárat…" />
                </SelectTrigger>
                <SelectContent>
                  {registers.length === 0 ? (
                    <div className="px-3 py-3 text-center text-xs text-muted-foreground">
                      Nincs aktív házipénztár a telephelyhez. Vegyél fel egyet a
                      Házipénztárak menüben.
                    </div>
                  ) : (
                    registers.map((r) => (
                      <SelectItem key={r._id} value={r._id}>
                        {r.name}
                        {r.company_name ? ` · ${r.company_name}` : ""}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Mégse
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving || (method === "cash" && !crId)}
            className="bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid="confirm-pay-efo"
          >
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            <CheckCircle2 className="mr-1.5 h-4 w-4" /> Kifizetés rögzítése
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
